import React, { useEffect, useState } from 'react';
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import { fetchServerWorldSettings, updateServerWorldSettings } from "src/actions/mine_craft/serversActions";
import { useAppDispatch } from "src/store/hooks";
import usePermissions from "src/hooks/usePermissions";

import { Button, Drawer, Input, InputNumber, Spin, Switch, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

// Scalar level.dat (NBT) entries with their types preserved — boolean
// stays a switch, number a number field. Keys can't be added or removed,
// only existing values changed.
interface WorldSettings {
  [key: string]: boolean | number | string;
}

// Editing the ACTIVE world requires a stopped server (the server
// overwrites level.dat on save) — the drawer warns, crafty's 409 is the
// hard gate.
const _WorldSettings = ({ server, world, active, onClose }: {
  server: ServerInterface,
  world: string | null,
  active: boolean,
  onClose: () => void
}) => {
  const dispatch = useAppDispatch();
  const { write_minecraft } = usePermissions();

  const [settings, setSettings] = useState<WorldSettings | null>(null);
  const [edits, setEdits] = useState<WorldSettings>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const serverId = server.id;
  // crafty-servers' status string is mirrored as sent, casing included.
  const running = (server.hosted_server?.status || '').toLowerCase() === 'running';

  useEffect(() => {
    if (!world) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      setSettings(null);
      setEdits({});
      setError(null);

      const resultAction = await dispatch(fetchServerWorldSettings({ id: serverId, world }));
      if (cancelled) return;

      setLoading(false);
      if (fetchServerWorldSettings.fulfilled.match(resultAction)) {
        setSettings(resultAction.payload?.settings || {});
      } else {
        const payload: any = resultAction.payload;
        setError(Array.isArray(payload) ? payload[0] : 'Could not read the world settings');
      }
    })().catch(console.error);

    return () => { cancelled = true; };
  }, [serverId, world, dispatch]);

  const dirty = Object.keys(edits).length > 0;

  function setValue(key: string, value: boolean | number | string) {
    setEdits(current => {
      const next = { ...current };

      if (settings && settings[key] === value) {
        delete next[key];
      } else {
        next[key] = value;
      }

      return next;
    });
  }

  async function save() {
    if (!world || !dirty) return;

    setSaving(true);
    const resultAction = await dispatch(updateServerWorldSettings({ id: serverId, world, settings: edits }));
    setSaving(false);

    if (updateServerWorldSettings.fulfilled.match(resultAction)) {
      message.success('World settings saved');
      setSettings(resultAction.payload?.settings || null);
      setEdits({});
    } else {
      const payload: any = resultAction.payload;
      message.error(Array.isArray(payload)
        ? payload[0]
        : 'Save failed — the active world only accepts edits while the server is stopped');
    }
  }

  const valueOf = (key: string) => (key in edits ? edits[key] : settings?.[key]);

  const Field = ({ name }: { name: string }) => {
    const value = valueOf(name);

    return (
      <div className={`o-row ${name in edits ? 'o-dirty' : ''}`}>
        <span className='o-key' title={name}>{name}</span>

        {typeof value === 'boolean' &&
          <Switch
            size='small'
            checked={value}
            disabled={!write_minecraft}
            onChange={next => setValue(name, next)}
          />}

        {typeof value === 'number' &&
          <InputNumber
            size='small'
            value={value}
            disabled={!write_minecraft}
            onChange={next => next !== null && setValue(name, next)}
          />}

        {typeof value === 'string' &&
          <Input
            size='small'
            className='o-text'
            value={value}
            disabled={!write_minecraft}
            onChange={event => setValue(name, event.target.value)}
          />}
      </div>
    );
  };

  return (
    <Drawer
      title={world ? `Settings — ${world}` : 'World settings'}
      open={!!world}
      onClose={onClose}
      width={440}
      className='v-srv-world-settings'
    >
      {active && running &&
        <div className='o-warning'>
          <ExclamationCircleOutlined />
          <span>
            This is the <b>active</b> world and the server is running — saves will be
            rejected until it stops (the server rewrites level.dat while it runs).
          </span>
        </div>
      }

      {loading && <div className='o-loading'><Spin /></div>}

      {error && !loading && <div className='o-error'>{error}</div>}

      {settings && !loading &&
        <>
          <div className='o-rows'>
            {Object.keys(settings).sort((a, b) => a.localeCompare(b)).map(name => (
              <Field key={name} name={name} />
            ))}
          </div>

          {write_minecraft &&
            <div className='o-foot'>
              <Button
                type='primary'
                loading={saving}
                disabled={!dirty}
                onClick={save}
              >
                Save {dirty ? `(${Object.keys(edits).length})` : ''}
              </Button>
              <Button disabled={!dirty || saving} onClick={() => setEdits({})}>
                Discard
              </Button>
            </div>
          }
        </>
      }
    </Drawer>
  );
}

export default _WorldSettings;
